import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import Login from './Login';
import Dashboard from './Dashboard';
import AddUsers from './pages/AddUsers';
import ModifyClients from './pages/ModifyClients';
import ModifyEditors from './pages/ModifyEditors';
import ModifyOperators from './pages/ModifyOperators';
import ModifyManagers from './pages/ModifyManagers';
import ModifyAdmins from './pages/ModifyAdmins';
import ModifyDesigners from './pages/ModifyDesigners';
import OperatorDashboard from './pages/OperatorDashboard';
import NewTasks from './pages/NewTasks';
import UpdateTask from './pages/UpdateTask';
import ViewTasks from './pages/ViewTasks';
import CheckInOut from './pages/CheckInOut';
import CheckInOutRecords from './pages/CheckInOutRecords';
import UserAttendance from './components/UserAttendance';
import DesignerTask from './pages/DesignerTask';
// Import Editor Pages
import MyTasks from './pages/editor/MyTasks';
import EditTask from './pages/editor/EditTask';
import AddTask from './pages/editor/AddTask';
import GetEditorAddedTasks from './pages/editor/GetEditorAddedTasks';
import EditEditorTask from './pages/editor/EditEditorTask';
// ✅ IMPORT ALL DESIGNER TASKS PAGE (NEW)
import AllDesignerTasks from './pages/AllDesignerTasks';
import UpdateDesignerTask from './pages/UpdateDesignerTask';
import DesignerDashboard from './pages/DesignerDashboard';
// Import New Editor Pages
import PendingTasks from './pages/editor/PendingTasks';
import CompletedTasks from './pages/editor/CompletedTasks';
import SubmitVideoForm from './pages/editor/SubmitVideoForm';
import AllEditorTasks from './pages/editor/AllEditorTasks';
import UnassignedEditorTasks from './pages/editor/UnassignedEditorTasks';
import EditorTasksPool from './pages/editor/EditorTasksPool';
import EditorTasksWithDesigners from './pages/editor/EditorTasksWithDesigners';
// Import New Pages
import TaskDetails from './pages/TaskDetails';
import ClientTasks from './pages/ClientTasks';
import EditorTasks from './pages/EditorTasks';
// Import New Task Management Pages
import AllTasks from './pages/AllTasks';
import AssignedTasksPage from './pages/AssignedTasks';
import UnassignedTasks from './pages/UnassignedTasks';
// ✅ IMPORT DESIGNER TASKS PAGE
import DesignerTasks from './pages/DesignerTasks';
// ✅ IMPORT MY WORK PAGE (Designer's task management)
import MyWork from './pages/MyWork';

// IMPORT CLIENT PAGES - Add these imports
import MyProjects from './pages/client/MyProjects';
import PendingProjects from './pages/client/PendingProjects';
import CompletedProjects from './pages/client/CompletedProjects';
import TaskFullDetails from './pages/client/TaskFullDetails';

// IMPORT OPERATOR TASKS PAGE - Add this import
import OperatorTasks from './pages/OperatorTasks';
// IMPORT TASKS BY USER PAGE - New Component
import TasksByUser from './pages/TasksByUser';
import './App.css';

function PrivateRoute({ children, allowedRoles = [] }) {
  const isAuth = sessionStorage.getItem('isAuth') === 'true';
  const userRole = sessionStorage.getItem('userRole');
  
  if (!isAuth) {
    return <Navigate to="/" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          
          {/* Dashboard - Accessible to all authenticated users */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          
          {/* CLIENT-SPECIFIC ROUTES */}
          <Route
            path="/my-projects"
            element={
              <PrivateRoute allowedRoles={['user', 'client']}>
                <MyProjects />
              </PrivateRoute>
            }
          />
          <Route
            path="/pending-projects"
            element={
              <PrivateRoute allowedRoles={['user', 'client']}>
                <PendingProjects />
              </PrivateRoute>
            }
          />
          <Route
            path="/completed-projects"
            element={
              <PrivateRoute allowedRoles={['user', 'client']}>
                <CompletedProjects />
              </PrivateRoute>
            }
          />
          
          {/* DESIGNER-SPECIFIC ROUTES */}
          <Route
            path="/my-work"
            element={
              <PrivateRoute allowedRoles={['designer']}>
                <MyWork />
              </PrivateRoute>
            }
          />
          
          {/* Task Full Details Route - Accessible to all roles */}
          <Route
            path="/task-full-details"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager', 'client', 'user', 'designer']}>
                <TaskFullDetails />
              </PrivateRoute>
            }
          />
          
          {/* ADD THIS ROUTE FOR COMPATIBILITY */}
          <Route
            path="/task-details"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager', 'client', 'user', 'designer']}>
                <TaskFullDetails />
              </PrivateRoute>
            }
          />
          
          {/* Operator-specific routes */}
          <Route
            path="/operator-dashboard"
            element={
              <PrivateRoute allowedRoles={['operator']}>
                <OperatorDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/new-tasks"
            element={
              <PrivateRoute allowedRoles={['operator','admin', 'manager']}>
                <NewTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/view-tasks"
            element={
              <PrivateRoute allowedRoles={['operator', 'admin', 'manager']}>
                <ViewTasks />
              </PrivateRoute>
            }
          />
          <Route path="/editor-tasks-with-designers" element={<EditorTasksWithDesigners />} />
          {/* ✅ FIXED: Added :taskId parameter to the route */}
          <Route
            path="/update-task/:taskId"
            element={
              <PrivateRoute allowedRoles={['operator', 'admin', 'manager']}>
                <UpdateTask />
              </PrivateRoute>
            }
          />

          {/* ADD OPERATOR TASKS ROUTE - This is the new route */}
          <Route
            path="/operator-tasks"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager']}>
                <OperatorTasks />
              </PrivateRoute>
            }
          />

          {/* Admin and Manager routes */}
          <Route
            path="/add-users"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <AddUsers />
              </PrivateRoute>
            }
          />
          <Route
            path="/modify-clients"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <ModifyClients />
              </PrivateRoute>
            }
          />
          <Route
            path="/modify-editors"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <ModifyEditors />
              </PrivateRoute>
            }
          />
          // Add this route
<Route path="/editor-tasks-pool" element={<GetEditorAddedTasks />} />
// ✅ CORRECT - Use proper path with parameter
<Route path="/edit-editor-task/:id" element={<EditEditorTask />} />
          <Route
            path="/modify-operators"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <ModifyOperators />
              </PrivateRoute>
            }
          />
          <Route path="/all-editor-tasks" element={<AllEditorTasks />} />
<Route path="/unassigned-editor-tasks" element={<UnassignedEditorTasks />} />
<Route path="/editor-tasks-pool" element={<EditorTasksPool />} />
<Route path="/edit-editor-task/:id" element={<EditEditorTask />} />
          <Route
            path="/modify-managers"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <ModifyManagers />
              </PrivateRoute>
            }
          />
           <Route
            path="/modify-designers"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <ModifyDesigners />
              </PrivateRoute>
            }
          />
          <Route
            path="/modify-admin"
            element={
              <PrivateRoute allowedRoles={[ 'manager','admin']}>
                <ModifyAdmins />
              </PrivateRoute>
            }
          />
          
          {/* ✅ ADD DESIGNER TASKS ROUTE - Admin and Manager only */}
          <Route
            path="/designer-tasks"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager','designer']}>
                <DesignerTasks />
              </PrivateRoute>
            }
          />
          
          <Route path="/update-designer-task" 
          element={<UpdateDesignerTask />} /> 
          <Route
            path="/all-designer-tasks"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <AllDesignerTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/designer-dashboard"
            element={
              <PrivateRoute allowedRoles={['designer']}>
                <DesignerDashboard />
              </PrivateRoute>
            }
          />
          {/* Task Management Routes - Admin and Manager only */}
          <Route
            path="/all-tasks"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <AllTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/assigned-tasks"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <AssignedTasksPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/unassigned-tasks"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <UnassignedTasks />
              </PrivateRoute>
            }
          />
          
          {/* ✅ NEW: Tasks By User Route - Admin and Manager only */}
          <Route
            path="/tasks-by-user"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <TasksByUser />
              </PrivateRoute>
            }
          />

          {/* Editor-specific routes */}
          <Route
            path="/my-tasks"
            element={
              <PrivateRoute allowedRoles={['editor']}>
                <MyTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/pending-tasks"
            element={
              <PrivateRoute allowedRoles={['editor']}>
                <PendingTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/completed-tasks"
            element={
              <PrivateRoute allowedRoles={['editor']}>
                <CompletedTasks />
              </PrivateRoute>
            }
          />
          <Route path="/add-task" element={<AddTask />} />
          <Route
            path="/submit-video"
            element={
              <PrivateRoute allowedRoles={['editor']}>
                <SubmitVideoForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/editor/edit-task/:taskId"
            element={
              <PrivateRoute allowedRoles={['editor']}>
                <EditTask />
              </PrivateRoute>
            }
          />

          {/* New Routes for Task Details, Client Tasks and Editor Tasks */}
          <Route
            path="/client-tasks"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager']}>
                <ClientTasks />
              </PrivateRoute>
            }
          />
          <Route
            path="/editor-tasks"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager']}>
                <EditorTasks />
              </PrivateRoute>
            }
          />
 <Route
            path="/designer-task"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager']}>
                <DesignerTask />
              </PrivateRoute>
            }
          />
          
          {/* Check-In/Out Route - All roles except client */}
          <Route
            path="/check-in-out"
            element={
              <PrivateRoute allowedRoles={['operator', 'editor', 'admin', 'manager', 'designer']}>
                <CheckInOut />
              </PrivateRoute>
            }
          />
           {/* Check-In/Out Route - All roles except client */}

          {/* Attendance Records Routes */}
          <Route
            path="/checkinout-records"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <CheckInOutRecords />
              </PrivateRoute>
            }
          />
          
          {/* User Attendance Route - Fixed with proper authentication */}
          <Route
            path="/user-attendance"
            element={
              <PrivateRoute allowedRoles={['admin', 'manager']}>
                <UserAttendance />
              </PrivateRoute>
            }
          />

          {/* Redirect any unknown routes to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;